import { Toggle } from "../Toggle";
import { t } from "../../i18n";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

export type JsonSchema = {
  type?: string;
  properties?: Record<string, JsonSchema>;
  required?: string[];
  title?: string;
  description?: string;
  default?: unknown;
  enum?: unknown[];
  secret?: boolean;
  widget?: "textarea";
};

const emptySchema = (): JsonSchema => ({
  type: "object",
  properties: {},
  required: [],
});
export function normalizeSchema(
  value: Record<string, unknown> | undefined,
): JsonSchema {
  const schema = (value ?? emptySchema()) as JsonSchema;
  return {
    type: "object",
    ...schema,
    properties: schema.properties ?? {},
    required: schema.required ?? [],
  };
}

export { SchemaFieldsEditor } from "../composer/SchemaFieldsEditor";
export { ConfigFields } from "../composer/ConfigFields";

export function SchemaFieldRow({
  name,
  property,
  required,
  onChange,
  onRemove,
}: {
  name: string;
  property: JsonSchema;
  required: boolean;
  onChange: (
    oldName: string,
    name: string,
    property: JsonSchema,
    required: boolean,
  ) => void;
  onRemove: () => void;
}) {
  const [draftName, setDraftName] = useState(name);
  useEffect(() => setDraftName(name), [name]);
  const select = Boolean(property.enum);
  const type = select
    ? "select"
    : property.widget === "textarea"
      ? "textarea"
      : (property.type ?? "string");
  const patch = (
    next: Partial<JsonSchema>,
    nextRequired = required,
    nextName = name,
  ) => onChange(name, nextName, { ...property, ...next }, nextRequired);
  return (
    <div className="grid gap-2 rounded-lg bg-stone-50 p-3 sm:grid-cols-6 dark:bg-white/3">
      <label className="sm:col-span-2">
        <span className="label">{t("Key")}</span>
        <input
          className="field"
          value={draftName}
          onChange={(event) =>
            setDraftName(event.target.value.replace(/\s+/g, "_"))
          }
          onBlur={() => {
            if (draftName && draftName !== name) patch({}, required, draftName);
          }}
        />
      </label>
      <label className="sm:col-span-2">
        <span className="label">{t("Label")}</span>
        <input
          className="field"
          value={property.title ?? ""}
          onChange={(event) => patch({ title: event.target.value })}
        />
      </label>
      <label>
        <span className="label">{t("Type")}</span>
        <select
          className="field"
          value={type}
          onChange={(event) =>
            patch(
              event.target.value === "select"
                ? { type: "string", enum: ["option"], widget: undefined }
                : event.target.value === "textarea"
                  ? { type: "string", enum: undefined, widget: "textarea" }
                  : {
                      type: event.target.value,
                      enum: undefined,
                      widget: undefined,
                    },
            )
          }
        >
          <option value="string">{t("Text")}</option>
          <option value="textarea">{t("Long text")}</option>
          <option value="number">{t("Number")}</option>
          <option value="integer">{t("Integer")}</option>
          <option value="boolean">{t("Checkbox")}</option>
          <option value="select">{t("Select")}</option>
        </select>
      </label>
      <div className="flex items-end gap-2 pb-2">
        <label className="flex items-center gap-1 text-xs">
          <Toggle
            checked={required}
            onChange={(event) => patch({}, event.target.checked)}
          />{" "}
          {t("Required")}
        </label>
        <button
          type="button"
          className="text-stone-400 hover:text-red-600"
          onClick={onRemove}
          title={t("Remove field")}
        >
          <Trash2 size={15} />
        </button>
      </div>
      <label className="sm:col-span-3">
        <span className="label">{t("Description")}</span>
        <input
          className="field"
          value={property.description ?? ""}
          onChange={(event) => patch({ description: event.target.value })}
        />
      </label>
      {type === "select" && (
        <label className="sm:col-span-3">
          <span className="label">{t("Options (one per line)")}</span>
          <textarea
            className="field"
            rows={2}
            value={(property.enum ?? []).join("\n")}
            onChange={(event) =>
              patch({
                enum: event.target.value
                  .split("\n")
                  .map((item) => item.trim())
                  .filter(Boolean),
              })
            }
          />
        </label>
      )}
      {type !== "boolean" && type !== "select" && (
        <label className="sm:col-span-2">
          <span className="label">{t("Default")}</span>
          <input
            className="field"
            type={["number", "integer"].includes(type) ? "number" : "text"}
            value={
              property.default === undefined ? "" : String(property.default)
            }
            onChange={(event) =>
              patch({
                default:
                  ["number", "integer"].includes(type) &&
                  event.target.value !== ""
                    ? Number(event.target.value)
                    : event.target.value,
              })
            }
          />
        </label>
      )}
      <label className="flex items-center gap-2 self-end pb-2 text-xs">
        <Toggle
          checked={property.secret ?? false}
          onChange={(event) => patch({ secret: event.target.checked })}
        />{" "}
        {t("Secret value")}
      </label>
    </div>
  );
}
